import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FormGrid,
  FormSection,
  FormSectionStack,
  MoneyField,
  RepeatableList,
} from "@/features/admin/components";
import { CatalogSelectField } from "@/features/admin/catalog";
import type { CurrencyRowValues } from "../../productForm";

/**
 * Los importes de UNA variante: uno por cada moneda activa del producto.
 *
 * Las monedas salen del paso de precios (`currencies`), no de una lista
 * fija: si aquí se nombraran MXN y USD a mano, un producto con precio en
 * euros tendría su costo extra sin importe en euros y no habría dónde
 * escribirlo. Es el mismo error que el mapa de precios ya evita.
 */
function ExtraCostAmounts({ name }: { name: string }) {
  const { t } = useTranslation();
  const rows = (useWatch({ name: "currencies" }) ?? []) as CurrencyRowValues[];

  const codes = rows
    .map((row) => row.code.trim().toUpperCase())
    .filter((code) => /^[A-Z]{3}$/.test(code));

  if (codes.length === 0) {
    return (
      <p className="text-[13px] leading-relaxed text-ds-ink-3">
        {t("Pon primero una moneda en el paso de Precios.")}
      </p>
    );
  }

  return (
    <>
      {codes.map((code) => (
        <MoneyField
          key={code}
          name={`${name}.amounts.${code}`}
          label={`${t("Costo extra")} ${code}`}
          currency={code}
        />
      ))}
    </>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Paso 6 · Variantes y costos adicionales, y solo eso.
 *
 * ── El ENVÍO ya no se edita aquí (26-ago, segunda pasada) ───────────
 * Anahí dictó que este paso lleva las variantes y los costos, nada
 * más. El peso y las medidas (`shipping.*`) siguen en el contrato y en
 * los valores del formulario —un producto que los tenga no los pierde
 * al guardar—, solo se quedaron sin control en el asistente.
 *
 * ── Qué es esto, y de dónde viene ───────────────────────────────────
 * Es lo que el sistema actual llama "Opciones": colores, tallas,
 * materiales y costos extra, repartidos allí en tres listas de
 * identificadores (`sizeIds`, `colorIds`, `extraCostIds`) que este
 * contrato dejó fuera a propósito mientras se pensaba. Vuelve como UNA
 * lista, porque las tres eran lo mismo: una opción con nombre y, a
 * veces, un sobreprecio.
 *
 * ── El nombre y la descripción son del CATÁLOGO ─────────────────────
 * Mismo `CatalogSelectField` que la categoría, el tipo y los beneficios,
 * con el `kind` cambiado. "Envío express" y qué significa se escriben
 * una vez y en los dos idiomas, no una vez por ficha: la alternativa era
 * copiar el mismo párrafo en cada producto que lo ofrezca y corregir la
 * errata en todos.
 *
 * ── El MONTO es del producto, y va POR MONEDA ───────────────────────
 * Esto es lo único que no puede vivir en el catálogo: el envío express
 * cuesta lo que cuesta ESTE envío. Y es un mapa como el resto del dinero
 * del sistema, no un número suelto, porque 200 no significa lo mismo en
 * pesos que en dólares — un solo número obligaría a elegir en qué moneda
 * está escrito, y en la otra tienda saldría veinte veces mal.
 *
 * En centavos enteros, como todo. Nadie divide entre 100.
 *
 * El mapa puede quedarse VACÍO: hay variantes que no cuestan nada —un
 * color distinto al mismo precio— y obligar a escribir 0 en cada moneda
 * para decir "no cuesta" es trabajo que no dice nada.
 *
 * ═══════════════════════════════════════════════════════════════════
 */
export function ExtrasStep() {
  const { t } = useTranslation();

  return (
    <FormSectionStack>
      <FormSection
        title={t("Variantes y costos adicionales")}
        description={t(
          "Colores, tallas, materiales o cargos aparte. El nombre y la descripción se comparten entre productos; el monto es de este.",
        )}
        columns={1}
        compact
      >
        <RepeatableList
          name="extraCosts"
          max={20}
          addLabel={t("Añadir variante")}
          itemLabel={(index) => t("Variante {{n}}", { n: index + 1 })}
          emptyHint={t(
            "Este producto se vende tal cual, sin variantes ni cargos aparte.",
          )}
          newItem={() => ({ value: "", amounts: {} })}
        >
          {({ name }) => (
            <FormGrid columns={3}>
              <CatalogSelectField
                name={`${name}.value`}
                kind="extra_cost"
                label={t("Qué es")}
                placeholder={t("Elige…")}
                addLabel={t("Crear una nueva")}
                /*
                  Con descripción, a diferencia de la categoría: "Talla
                  XL" no se explica solo, y quien compra tiene que leer
                  qué está eligiendo.
                */
                describable
              />
              <ExtraCostAmounts name={name} />
            </FormGrid>
          )}
        </RepeatableList>
      </FormSection>
    </FormSectionStack>
  );
}
